import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  attendanceCreateWorkSchedule,
  attendanceDeleteWorkSchedule,
  attendanceLoadShifts,
  attendanceLoadWorkSchedules,
  attendanceUpdateWorkSchedule,
  type AttendanceShift,
  type AttendanceWorkSchedule,
  type AttendanceWorkScheduleWeekPattern,
} from '../../lib/apiPhpAdapter'
import './AttendanceOrgAdmin.css'

type Props = {
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  embedded?: boolean
  onChanged?: () => void | Promise<void>
}

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'

const DAYS: Array<{ key: DayKey; label: string }> = [
  { key: 'mon', label: 'Po' },
  { key: 'tue', label: 'Út' },
  { key: 'wed', label: 'St' },
  { key: 'thu', label: 'Čt' },
  { key: 'fri', label: 'Pá' },
  { key: 'sat', label: 'So' },
  { key: 'sun', label: 'Ne' },
]

type FormState = {
  name: string
  week_pattern: AttendanceWorkScheduleWeekPattern
  days: Record<DayKey, string>
  daysEven: Record<DayKey, string>
  leave_dovolena_hours: string
  leave_samostudium_days: string
  leave_nemoc_days: string
  active: boolean
}

const emptyDays = (): Record<DayKey, string> => ({
  mon: '',
  tue: '',
  wed: '',
  thu: '',
  fri: '',
  sat: '',
  sun: '',
})

const emptyForm = (): FormState => ({
  name: '',
  week_pattern: 'all',
  days: emptyDays(),
  daysEven: emptyDays(),
  leave_dovolena_hours: '',
  leave_samostudium_days: '',
  leave_nemoc_days: '',
  active: true,
})

function leaveToInput(raw: number | string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return ''
  const n = Number(raw)
  return Number.isFinite(n) ? String(n) : ''
}

function leaveToPayload(raw: string): number | null {
  const t = raw.trim()
  if (t === '') return null
  const n = parseFloat(t.replace(',', '.'))
  return Number.isFinite(n) && n >= 0 ? n : null
}

function idToSelect(raw: number | null | undefined): string {
  return raw != null && Number(raw) > 0 ? String(raw) : ''
}

function formFromSchedule(s: AttendanceWorkSchedule): FormState {
  return {
    name: s.name || '',
    week_pattern: s.week_pattern === 'odd_even' ? 'odd_even' : 'all',
    days: {
      mon: idToSelect(s.mon_shift_id),
      tue: idToSelect(s.tue_shift_id),
      wed: idToSelect(s.wed_shift_id),
      thu: idToSelect(s.thu_shift_id),
      fri: idToSelect(s.fri_shift_id),
      sat: idToSelect(s.sat_shift_id),
      sun: idToSelect(s.sun_shift_id),
    },
    daysEven: {
      mon: idToSelect(s.mon_even_shift_id),
      tue: idToSelect(s.tue_even_shift_id),
      wed: idToSelect(s.wed_even_shift_id),
      thu: idToSelect(s.thu_even_shift_id),
      fri: idToSelect(s.fri_even_shift_id),
      sat: idToSelect(s.sat_even_shift_id),
      sun: idToSelect(s.sun_even_shift_id),
    },
    leave_dovolena_hours: leaveToInput(s.leave_dovolena_hours),
    leave_samostudium_days: leaveToInput(s.leave_samostudium_days),
    leave_nemoc_days: leaveToInput(s.leave_nemoc_days),
    active: s.active === undefined || s.active === null ? true : Boolean(Number(s.active)),
  }
}

function formToPayload(form: FormState) {
  const parse = (v: string) => (v ? parseInt(v, 10) : null)
  const payload: Record<string, unknown> = {
    name: form.name.trim(),
    week_pattern: form.week_pattern,
    active: form.active,
    mon_shift_id: parse(form.days.mon),
    tue_shift_id: parse(form.days.tue),
    wed_shift_id: parse(form.days.wed),
    thu_shift_id: parse(form.days.thu),
    fri_shift_id: parse(form.days.fri),
    sat_shift_id: parse(form.days.sat),
    sun_shift_id: parse(form.days.sun),
  }
  if (form.week_pattern === 'odd_even') {
    payload.mon_even_shift_id = parse(form.daysEven.mon)
    payload.tue_even_shift_id = parse(form.daysEven.tue)
    payload.wed_even_shift_id = parse(form.daysEven.wed)
    payload.thu_even_shift_id = parse(form.daysEven.thu)
    payload.fri_even_shift_id = parse(form.daysEven.fri)
    payload.sat_even_shift_id = parse(form.daysEven.sat)
    payload.sun_even_shift_id = parse(form.daysEven.sun)
  } else {
    payload.mon_even_shift_id = null
    payload.tue_even_shift_id = null
    payload.wed_even_shift_id = null
    payload.thu_even_shift_id = null
    payload.fri_even_shift_id = null
    payload.sat_even_shift_id = null
    payload.sun_even_shift_id = null
  }
  payload.leave_dovolena_hours = leaveToPayload(form.leave_dovolena_hours)
  payload.leave_samostudium_days = leaveToPayload(form.leave_samostudium_days)
  payload.leave_nemoc_days = leaveToPayload(form.leave_nemoc_days)
  return payload
}

export function AttendanceWorkSchedulesPanel({ showAlert, embedded = false, onChanged }: Props) {
  const [items, setItems] = useState<AttendanceWorkSchedule[]>([])
  const [shifts, setShifts] = useState<AttendanceShift[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState<FormState>(emptyForm)
  const [editItem, setEditItem] = useState<AttendanceWorkSchedule | null>(null)
  const [editForm, setEditForm] = useState<FormState>(emptyForm)
  const [deleteItem, setDeleteItem] = useState<AttendanceWorkSchedule | null>(null)

  const shiftNameById = useMemo(() => {
    const m = new Map<number, string>()
    for (const s of shifts) m.set(Number(s.id), s.name)
    return m
  }, [shifts])

  const shiftOptions = useMemo(
    () => [{ id: '', name: '—' }, ...shifts.filter((s) => !!s.active).map((s) => ({ id: String(s.id), name: s.name }))],
    [shifts],
  )

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [list, sh] = await Promise.all([attendanceLoadWorkSchedules(), attendanceLoadShifts()])
      setItems(Array.isArray(list) ? list : [])
      setShifts(Array.isArray(sh) ? sh : [])
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst pozice', 'error')
    } finally {
      setLoading(false)
    }
  }, [showAlert])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    if (!createOpen && !editItem && !deleteItem) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        setCreateOpen(false)
        setEditItem(null)
        setDeleteItem(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [createOpen, editItem, deleteItem, busy])

  const openCreate = () => {
    setEditItem(null)
    setDeleteItem(null)
    setCreateForm(emptyForm())
    setCreateOpen(true)
  }

  const openEdit = (it: AttendanceWorkSchedule) => {
    setCreateOpen(false)
    setDeleteItem(null)
    setEditItem(it)
    setEditForm(formFromSchedule(it))
  }

  const openDelete = (it: AttendanceWorkSchedule) => {
    setCreateOpen(false)
    setEditItem(null)
    setDeleteItem(it)
  }

  const labelShift = (id: number | null | undefined) => {
    if (id == null || !Number(id)) return '—'
    return shiftNameById.get(Number(id)) || `#${id}`
  }

  const formatWeekRow = (s: AttendanceWorkSchedule, even: boolean) => {
    const ids = even
      ? [s.mon_even_shift_id, s.tue_even_shift_id, s.wed_even_shift_id, s.thu_even_shift_id, s.fri_even_shift_id]
      : [s.mon_shift_id, s.tue_shift_id, s.wed_shift_id, s.thu_shift_id, s.fri_shift_id]
    return ids.map((id) => labelShift(id)).join(' / ')
  }

  const onCreate = async () => {
    if (!createForm.name.trim()) {
      showAlert('Název je povinný', 'warning')
      return
    }
    setBusy(true)
    try {
      await attendanceCreateWorkSchedule(formToPayload(createForm) as Partial<AttendanceWorkSchedule> & { name: string })
      showAlert('Pozice vytvořena', 'success')
      setCreateOpen(false)
      setCreateForm(emptyForm())
      await reload()
      await onChanged?.()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const onSaveEdit = async () => {
    if (!editItem) return
    if (!editForm.name.trim()) {
      showAlert('Název je povinný', 'warning')
      return
    }
    setBusy(true)
    try {
      await attendanceUpdateWorkSchedule(editItem.id, formToPayload(editForm))
      showAlert('Pozice upravena', 'success')
      setEditItem(null)
      await reload()
      await onChanged?.()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const onConfirmDelete = async () => {
    if (!deleteItem) return
    setBusy(true)
    try {
      await attendanceDeleteWorkSchedule(deleteItem.id)
      showAlert('Smazáno', 'success')
      setDeleteItem(null)
      await reload()
      await onChanged?.()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Smazání selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const renderDaySelects = (
    form: FormState,
    setForm: (fn: (f: FormState) => FormState) => void,
    which: 'days' | 'daysEven',
  ) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
        gap: 8,
      }}
    >
      {DAYS.map(({ key, label }) => (
        <label key={`${which}-${key}`}>
          {label}
          <select
            value={form[which][key]}
            disabled={busy}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                [which]: { ...f[which], [key]: e.target.value },
              }))
            }
            style={{ width: '100%', marginTop: 4 }}
          >
            {shiftOptions.map((o) => (
              <option key={o.id || 'empty'} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
      ))}
    </div>
  )

  const renderFormBody = (form: FormState, setForm: (fn: (f: FormState) => FormState) => void, opts?: { showActive?: boolean }) => (
    <div className="att-abs-modal__fields">
      <label>
        Název
        <input
          value={form.name}
          disabled={busy}
          autoFocus
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
      </label>
      <label>
        Typ týdne
        <select
          value={form.week_pattern}
          disabled={busy}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              week_pattern: e.target.value === 'odd_even' ? 'odd_even' : 'all',
            }))
          }
        >
          <option value="all">Stejný každý týden</option>
          <option value="odd_even">Liché / sudé ISO týdny</option>
        </select>
      </label>
      {form.week_pattern === 'odd_even' ? (
        <>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>Lichý týden</div>
          {renderDaySelects(form, setForm, 'days')}
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', marginTop: 4 }}>Sudý týden</div>
          {renderDaySelects(form, setForm, 'daysEven')}
        </>
      ) : (
        <>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>Po–Ne</div>
          {renderDaySelects(form, setForm, 'days')}
        </>
      )}
      <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
        Lichý/sudý se počítá podle čísla ISO týdne (Evropa/Praha).
      </p>
      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>
        Nároky (volitelné)
      </div>
      <div className="att-abs-modal__time-row att-abs-modal__time-row--2">
        <label>
          Dovolená (h)
          <input
            type="number"
            min={0}
            step={0.5}
            value={form.leave_dovolena_hours}
            disabled={busy}
            placeholder="—"
            onChange={(e) => setForm((f) => ({ ...f, leave_dovolena_hours: e.target.value }))}
          />
        </label>
        <label>
          Samostudium (dny)
          <input
            type="number"
            min={0}
            step={0.5}
            value={form.leave_samostudium_days}
            disabled={busy}
            placeholder="—"
            onChange={(e) => setForm((f) => ({ ...f, leave_samostudium_days: e.target.value }))}
          />
        </label>
      </div>
      <label>
        Nemoc / sick days (dny)
        <input
          type="number"
          min={0}
          step={0.5}
          value={form.leave_nemoc_days}
          disabled={busy}
          placeholder="—"
          onChange={(e) => setForm((f) => ({ ...f, leave_nemoc_days: e.target.value }))}
        />
      </label>
      <p className="att-abs-modal__hint">
        Při přiřazení zaměstnance na pozici se prázdné roční nároky doplní z této šablony.
      </p>
      {opts?.showActive ? (
        <label className="att-abs-modal__check">
          <input
            type="checkbox"
            checked={form.active}
            disabled={busy}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
          />
          aktivní
        </label>
      ) : null}
    </div>
  )

  const Wrapper = embedded ? 'div' : 'section'
  const wrapperClass = embedded ? 'att-cat' : 'attendance-panel att-cat'

  return (
    <Wrapper className={wrapperClass}>
      {!embedded ? <h2>Pozice</h2> : null}
      <p className="att-cat__intro">
        Pozice = týdenní rozvrh směn (Po–Ne) a volitelné roční nároky (dovolená, samostudium, nemoc).
        Nejdřív založte směny, pak pozici.
      </p>

      <div className="att-org__add att-cat__add">
        <button type="button" className="system-button" disabled={busy} onClick={openCreate}>
          Přidat pozici
        </button>
      </div>

      {loading ? <p className={embedded ? 'att-prehled__muted' : 'attendance-muted'}>Načítání…</p> : null}

      <div className="att-cat__wrap">
        <table className="att-cat__table att-cat__table--schedules">
          <thead>
            <tr>
              <th className="att-cat__col-name">Pozice</th>
              <th className="att-cat__col-narrow">Týden</th>
              <th className="att-cat__col-week att-cat__col-fill">Po–Ne</th>
              <th className="att-cat__col-narrow">DOV h</th>
              <th className="att-cat__col-narrow">SAM</th>
              <th className="att-cat__col-narrow">Nemoc</th>
              <th className="att-cat__col-narrow">Stav</th>
              <th className="att-cat__col-actions" aria-label="Akce" />
            </tr>
          </thead>
          <tbody>
            {items.map((s) => {
              const isActive = s.active === undefined || s.active === null ? true : Boolean(Number(s.active))
              const oddEven = s.week_pattern === 'odd_even'
              const weekText = oddEven
                ? `L: ${formatWeekRow(s, false)} · S: ${formatWeekRow(s, true)}`
                : formatWeekRow(s, false)
              return (
                <tr key={s.id} className={isActive ? undefined : 'att-cat__row--inactive'}>
                  <td className="att-cat__col-name" title={s.name}>
                    {s.name}
                  </td>
                  <td className="att-cat__col-narrow">{oddEven ? 'L/S' : 'stejný'}</td>
                  <td className="att-cat__col-week att-cat__week" title={weekText}>
                    {oddEven ? (
                      <>
                        <div>
                          <span className="att-cat__week-label">L: </span>
                          {formatWeekRow(s, false)}
                        </div>
                        <div>
                          <span className="att-cat__week-label">S: </span>
                          {formatWeekRow(s, true)}
                        </div>
                      </>
                    ) : (
                      formatWeekRow(s, false)
                    )}
                  </td>
                  <td className="att-cat__col-narrow">{leaveToInput(s.leave_dovolena_hours) || '—'}</td>
                  <td className="att-cat__col-narrow">{leaveToInput(s.leave_samostudium_days) || '—'}</td>
                  <td className="att-cat__col-narrow">{leaveToInput(s.leave_nemoc_days) || '—'}</td>
                  <td className="att-cat__col-narrow">{isActive ? 'aktivní' : 'neaktivní'}</td>
                  <td className="att-cat__col-actions">
                    <button
                      type="button"
                      className="system-button att-cat__btn"
                      disabled={busy}
                      onClick={() => openEdit(s)}
                    >
                      Upravit
                    </button>
                    <button
                      type="button"
                      className="system-button att-cat__btn att-cat__btn--danger"
                      disabled={busy}
                      onClick={() => openDelete(s)}
                    >
                      Smazat
                    </button>
                  </td>
                </tr>
              )
            })}
            {!loading && items.length === 0 ? (
              <tr>
                <td colSpan={8} className="att-cat__empty">
                  Žádné pozice.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {createOpen ? (
        <div
          className="att-abs-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setCreateOpen(false)
          }}
        >
          <div
            className="att-abs-modal"
            style={{ width: 'min(720px, 100%)' }}
            role="dialog"
            aria-modal
            aria-labelledby="att-ws-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="att-ws-create-title">Nová pozice</h3>
            {renderFormBody(createForm, setCreateForm)}
            <div className="att-abs-modal__actions">
              <button
                type="button"
                className="system-button"
                disabled={busy || !createForm.name.trim()}
                onClick={() => void onCreate()}
              >
                {busy ? 'Ukládám…' : 'Vytvořit'}
              </button>
              <button
                type="button"
                className="system-button"
                style={{ background: '#64748b' }}
                disabled={busy}
                onClick={() => setCreateOpen(false)}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editItem ? (
        <div
          className="att-abs-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setEditItem(null)
          }}
        >
          <div
            className="att-abs-modal"
            style={{ width: 'min(720px, 100%)' }}
            role="dialog"
            aria-modal
            aria-labelledby="att-ws-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="att-ws-edit-title">Upravit pozici</h3>
            {renderFormBody(editForm, setEditForm, { showActive: true })}
            <div className="att-abs-modal__actions">
              <button
                type="button"
                className="system-button"
                disabled={busy || !editForm.name.trim()}
                onClick={() => void onSaveEdit()}
              >
                {busy ? 'Ukládám…' : 'Uložit'}
              </button>
              <button
                type="button"
                className="system-button"
                style={{ background: '#64748b' }}
                disabled={busy}
                onClick={() => setEditItem(null)}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteItem ? (
        <div
          className="att-abs-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setDeleteItem(null)
          }}
        >
          <div
            className="att-abs-modal att-abs-modal--danger"
            role="dialog"
            aria-modal
            aria-labelledby="att-ws-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="att-ws-delete-title">Smazat pozici?</h3>
            <p className="att-abs-modal__warn">
              Opravdu chcete smazat pozici <strong>{deleteItem.name}</strong>?
            </p>
            <p className="att-abs-modal__warn-sub">
              Pokud je pozice přiřazena zaměstnancům, smazání může selhat.
            </p>
            <div className="att-abs-modal__actions">
              <button
                type="button"
                className="system-button"
                style={{ background: '#ef4444' }}
                disabled={busy}
                onClick={() => void onConfirmDelete()}
              >
                {busy ? 'Mazání…' : 'Ano, smazat'}
              </button>
              <button
                type="button"
                className="system-button"
                style={{ background: '#64748b' }}
                disabled={busy}
                onClick={() => setDeleteItem(null)}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Wrapper>
  )
}
